import type { LocalRow, SeedTables } from '../types/localDb.types'
import { type RoleCode, roleId, seedAudit } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Pembangun seed Workflow Studio generik (pola `workflow-qc.seed.ts`) untuk workflow C07 — satu spesifikasi tabel
 * TL 12B §1–§8 menghasilkan tabel `wml_*`. Id dirancang stabil: `scoped(n) = no * 1000 + n`.
 * Tabel email, tabbing, SLA, dan cross-workflow tidak di-seed karena tidak dijalankan local mode.
 */

type Condition = Record<string, unknown> | null

export type WorkflowSpec<S extends string, A extends string, N extends string, C extends string> = {
  /** Nomor workflow (unik per workflow; WF-PRC=1 … WF-REQ=5). */
  no: number
  code: string
  name: string
  entity: string
  /** [code, name, is_start, is_terminal, color, desc] — urutan array = `order`. */
  statuses: readonly (readonly [S, string, boolean, boolean, string, string])[]
  /** [code, name, role, CASL condition]. */
  actors: readonly (readonly [A, string, RoleCode, Condition])[]
  /** [code, name, type, ui_config]. */
  components: readonly (readonly [C, string, 'ACTION' | 'PANEL', Record<string, unknown>])[]
  /** [name, from, to, component, is_need_reason, is_reason_required, entity_patch_mapping]. */
  actions: readonly (readonly [N, S, S, C, boolean, boolean, Record<string, unknown> | null])[]
  eligibility: readonly (readonly [N, A])[]
  /** [komponen panel, status, permission, actor | null (=semua actor)]. */
  panels: readonly (readonly [C, S, 'read' | 'write', A | null])[]
  /** Status yang memicu notifikasi email. */
  emailStatuses?: readonly S[]
}

export function defineWorkflow<S extends string, A extends string, N extends string, C extends string>(
  spec: WorkflowSpec<S, A, N, C>
) {
  const scoped = (n: number) => spec.no * 1000 + n
  const workflowId = seedId(SEED_GROUP.workflow, spec.no)
  const statusId = (code: S) => seedId(SEED_GROUP.wmlStatus, scoped(spec.statuses.findIndex(([c]) => c === code) + 1))
  const actorId = (code: A) => seedId(SEED_GROUP.wmlActor, scoped(spec.actors.findIndex(([c]) => c === code) + 1))
  const componentId = (code: C) =>
    seedId(SEED_GROUP.wmlComponent, scoped(spec.components.findIndex(([c]) => c === code) + 1))
  const actionId = (name: N) => seedId(SEED_GROUP.wmlAction, scoped(spec.actions.findIndex(([n]) => n === name) + 1))
  const actionStatus = Object.fromEntries(spec.actions.map(([name, from, to]) => [name, [from, to]])) as Record<
    N,
    [S, S]
  >

  const build = (): SeedTables => {
    const panels: LocalRow[] = spec.panels.map(([component, status, permission], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlPanel, scoped(index + 1)),
      component_id: componentId(component),
      status_id: statusId(status),
      permission
    }))
    return {
      wml_workflow: [
        {
          ...seedAudit,
          id: workflowId,
          code: spec.code,
          name: spec.name,
          entity: spec.entity,
          source_policy: 'manual_create'
        }
      ],
      wml_status: spec.statuses.map(([code, name, isStart, isTerminal, color, desc], index) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.wmlStatus, scoped(index + 1)),
        workflow_id: workflowId,
        code,
        name,
        order: index + 1,
        is_start: isStart,
        is_terminal: isTerminal,
        is_send_email: (spec.emailStatuses ?? []).includes(code),
        color,
        desc
      })),
      wml_actor: spec.actors.map(([code, name, role, condition]) => ({
        ...seedAudit,
        id: actorId(code),
        workflow_id: workflowId,
        code,
        name,
        role_id: roleId(role),
        job_fn_id: null,
        condition
      })),
      wml_component: spec.components.map(([code, name, type, uiConfig]) => ({
        ...seedAudit,
        id: componentId(code),
        workflow_id: workflowId,
        code,
        name,
        type,
        ui_config: uiConfig
      })),
      wml_action: spec.actions.map(([name, from, to, component, needReason, reasonRequired, patch]) => ({
        ...seedAudit,
        id: actionId(name),
        workflow_id: workflowId,
        name,
        from_status_id: statusId(from),
        to_status_id: statusId(to),
        component_id: componentId(component),
        is_need_reason: needReason,
        is_reason_required: reasonRequired,
        is_post_closure: false,
        entity_patch_mapping: patch
      })),
      wml_action_actor_eligibility: spec.eligibility.map(([action, actor], index) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.wmlActionActor, scoped(index + 1)),
        action_id: actionId(action),
        actor_id: actorId(actor)
      })),
      wml_panel: panels,
      wml_panel_actor_eligibility: spec.panels.flatMap(([, , , actor], index) =>
        (actor ? [actor] : spec.actors.map(([code]) => code)).map((actorCode, offset) => ({
          ...seedAudit,
          id: seedId(SEED_GROUP.wmlPanelActor, scoped((index + 1) * 10 + offset)),
          panel_id: panels[index].id,
          actor_id: actorId(actorCode)
        }))
      )
    }
  }

  return { spec, workflowId, statusId, actorId, actionId, actionStatus, build }
}
